import { nextTick } from "vue";
import { apiConnection } from "@/services/api-connection";
import { i18n } from "@/i18n";
import { notificationManager } from "@/services/utilities/notification-manager";
import { formValidator } from "@/services/form/form-validator";
import router from "@/router";
import { questionnaireStore, triadStore } from "@/main";
import { userManager } from "@/services/security/user-manager";
import { questionnaireApiService } from "@/services/questionnaire/questionnaire-api-service";
import { questionnaireModelBuilder } from "@/services/questionnaire/questionnaire-model-builder";
import { questionnaireValidator } from "@/services/questionnaire/questionnaire-validator";
import { questionnaireRadarService } from "@/services/questionnaire/questionnaire-radar-service";

class QuestionnaireManager {
  DUPLICATION_TYPE_ADD = 1;
  DUPLICATION_TYPE_REPLACE = 2;
  DUPLICATION_TYPE_OVERWRITE = 3;
  USE_CUSTOM_DATE = 1;
  USE_CREW_DATE = 2;
  USE_CONTRACT_DATE = 3;
  QUESTIONNAIRE_REPLY_STATUS_DRAFTED = 1;
  QUESTIONNAIRE_REPLY_STATUS_SHARED = 2;
  QUESTIONNAIRE_REPLY_STATUS_SIGNED = 3;
  DRAFT_WAIT_TIMEOUT_MS = 10000;
  _signingInProgress = false;
  _draftInFlight = null;

  // --- Appels API (cf. questionnaire-api-service.js) ---
  postAdminQuestionnaire(...args) {
    return questionnaireApiService.postAdminQuestionnaire(...args);
  }

  getAdminQuestionnaireList(...args) {
    return questionnaireApiService.getAdminQuestionnaireList(...args);
  }

  getAdminQuestionnaire(...args) {
    return questionnaireApiService.getAdminQuestionnaire(...args);
  }

  loadQuestionnaires(...args) {
    return questionnaireApiService.loadQuestionnaires(...args);
  }

  loadQuestionnaire(...args) {
    return questionnaireApiService.loadQuestionnaire(...args);
  }

  getPdf(...args) {
    return questionnaireApiService.getPdf(...args);
  }

  postQuestionnaire(...args) {
    return questionnaireApiService.postQuestionnaire(...args);
  }

  addRow(...args) {
    return questionnaireApiService.addRow(...args);
  }

  copyQuestionnaire(...args) {
    return questionnaireApiService.copyQuestionnaire(...args);
  }

  removeQuestionnaire(...args) {
    return questionnaireApiService.removeQuestionnaire(...args);
  }

  addComment(...args) {
    return questionnaireApiService.addComment(...args);
  }

  toggleQuestionnaireActive(...args) {
    return questionnaireApiService.toggleQuestionnaireActive(...args);
  }

  // --- Construction du form-model (cf. questionnaire-model-builder.js) ---
  getFieldBlockPrefixes(...args) {
    return questionnaireModelBuilder.getFieldBlockPrefixes(...args);
  }

  buildModel(...args) {
    return questionnaireModelBuilder.buildModel(...args);
  }

  buildField(...args) {
    return questionnaireModelBuilder.buildField(...args);
  }

  isValidAnswerForFieldType(...args) {
    return questionnaireModelBuilder.isValidAnswerForFieldType(...args);
  }

  addClusterLine(...args) {
    return questionnaireModelBuilder.addClusterLine(...args);
  }

  isEnabled(...args) {
    return questionnaireModelBuilder.isEnabled(...args);
  }

  isChoice(...args) {
    return questionnaireModelBuilder.isChoice(...args);
  }

  hasOtherQuestionOfType(...args) {
    return questionnaireModelBuilder.hasOtherQuestionOfType(...args);
  }

  help(...args) {
    return questionnaireModelBuilder.help(...args);
  }

  disabled(...args) {
    return questionnaireModelBuilder.disabled(...args);
  }

  isCheckbox(...args) {
    return questionnaireModelBuilder.isCheckbox(...args);
  }

  getColumnMaxWidth(...args) {
    return questionnaireModelBuilder.getColumnMaxWidth(...args);
  }

  // --- Validation (cf. questionnaire-validator.js) ---
  validateMissionFields(...args) {
    return questionnaireValidator.validateMissionFields(...args);
  }

  validateActivityFields(...args) {
    return questionnaireValidator.validateActivityFields(...args);
  }

  validQuestion(...args) {
    return questionnaireValidator.validQuestion(...args);
  }

  // --- Radar de cluster (cf. questionnaire-radar-service.js) ---
  extractMark(...args) {
    return questionnaireRadarService.extractMark(...args);
  }

  collectRadarProfiles(...args) {
    return questionnaireRadarService.collectRadarProfiles(...args);
  }

  getProfileLabel(...args) {
    return questionnaireRadarService.getProfileLabel(...args);
  }

  buildRadarSeries(...args) {
    return questionnaireRadarService.buildRadarSeries(...args);
  }

  // --- Orchestration : sauvegarde et traitement du questionnaire ---
  async saveQuestionnaire(replyStatus, message, error, triadId, questionnaireId) {
    if (replyStatus === this.QUESTIONNAIRE_REPLY_STATUS_DRAFTED && this._signingInProgress) {
      return Promise.resolve(null);
    }
    if (replyStatus >= this.QUESTIONNAIRE_REPLY_STATUS_SHARED) {
      this._signingInProgress = true;
    }
    if (replyStatus >= this.QUESTIONNAIRE_REPLY_STATUS_SHARED && this._draftInFlight) {
      // Un brouillon qui ne se resout jamais (reseau mobile coupe, app passee en arriere-plan)
      // laisserait sinon l'action « Signer » bloquee sans aucun retour a l'utilisateur.
      await Promise.race([
        this._draftInFlight,
        new Promise((resolve) => setTimeout(resolve, this.DRAFT_WAIT_TIMEOUT_MS)),
      ]);
    }

    let validation = [];
    let missionErrors = [];
    let activityErrors = [];
    if (replyStatus === this.QUESTIONNAIRE_REPLY_STATUS_SIGNED) {
      // setFocus=false ici : le focus est posé une seule fois plus bas, une fois les erreurs
      // Mission/Activité connues, pour éviter deux scrolls/focus concurrents (TA-4947).
      validation = formValidator.validForm(questionnaireStore.model, true, true, false);
      missionErrors = questionnaireValidator.validateMissionFields();
      activityErrors = questionnaireValidator.validateActivityFields();
      const activitySigningErrors = [...missionErrors, ...activityErrors];
      // validateActivityFields() incrémente model.vars.validationAttempt, qui rouvre (via
      // expandTrigger) la carte Activité en défaut - mais son champ (v-if="expanded") n'existe dans
      // le DOM qu'après le prochain rendu Vue : sans ce nextTick, focusErrorInput() ne trouvait rien
      // à scroller/focuser (TA-4947).
      await nextTick();
      // Les erreurs de formulaire restent prioritaires sur celles de Mission/Activité : elles sont
      // placées en tête du tableau passé à focusErrorInput(), qui ne scroll/focus que le 1er élément.
      formValidator.focusErrorInput([...(validation.errors || []), ...activitySigningErrors], true);
      validation.errors = [...(validation.errors || []), ...activitySigningErrors];
    }

    const resultPromise = new Promise((resolve) => {
      if ((validation.errors && !validation.errors.length) || replyStatus !== 3) {
        const modelToSave = {};

        Object.values(questionnaireStore.model).forEach((question) => {
          modelToSave[question.vars.id] = {};
          let value;
          if (this.isCheckbox(question)) {
            value = [];
            Object.values(question.vars.value).forEach((choice) => {
              if (typeof choice !== "string" && choice && choice.checked) {
                value.push(choice.value);
              } else if (typeof choice === "string") {
                value.push(choice);
              }
            });
          } else {
            value = question.vars.value;
          }

          modelToSave[question.vars.id]["answers"] = value;

          if (question.vars.answerComment) {
            modelToSave[question.vars.id]["answerComment"] = question.vars.answerComment;
          }
          if (question.vars.answerId) {
            modelToSave[question.vars.id]["answerId"] = question.vars.answerId;
          }
          if (question.name) {
            modelToSave[question.vars.id]["name"] = question.name;
          }
          if (question.vars.comments) {
            modelToSave[question.vars.id]["comments"] = question.vars.comments;
          }
          if (question.vars.missionsEvolutions) {
            modelToSave[question.vars.id]["missionsEvolutions"] = question.vars.missionsEvolutions;
          }
          if (question.vars.activitiesEvolutions) {
            modelToSave[question.vars.id]["activitiesEvolutions"] = question.vars.activitiesEvolutions;
          }
          if (question.cluster) {
            modelToSave[question.vars.id]["cluster"] = question.cluster;
          }
          if (question.delete) {
            modelToSave[question.vars.id]["delete"] = true;
          }
        });

        let profile = questionnaireStore.questionnaire.userProfile;

        if (!profile && triadStore.triad.userRoles && triadStore.triad.userRoles[0]) {
          profile = triadStore.triad.userRoles[0];
        }

        this.postQuestionnaire(questionnaireId, triadId, replyStatus, modelToSave, profile)
          .then((response) => {
            if (response && !response.error) {
              Object.keys(modelToSave).forEach((id) => {
                if (id.includes("new-")) {
                  delete modelToSave[id];
                }
              });
              if (replyStatus !== this.QUESTIONNAIRE_REPLY_STATUS_DRAFTED) {
                notificationManager.showNotification("success", message);
                const magicLink = apiConnection.getMagicLinkToken();
                if (!magicLink) {
                  questionnaireStore.fetchQuestionnaires(triadId);
                  router.push({ name: "Dashboard" });
                } else {
                  questionnaireStore.fetchQuestionnaire({
                    refresh: true,
                    questionnaire: questionnaireId,
                    triad: triadId,
                  });
                }
              } else {
                questionnaireStore.updateQuestionnaireStatus(1);
                notificationManager.showNotification("success", message);
              }
              resolve(response);
            } else {
              notificationManager.showNotification("error", response?.message);
              resolve(response ?? { error: true });
            }
          })
          .catch((error) => {
            if (!error?.handled) {
              const errorMessage = error?.response?.data?.message;
              notificationManager.showNotification("error", errorMessage || error.message);
            }
            resolve({ error: true, exception: error });
          })
          .finally(() => {
            // Seul l'appel qui a pose le drapeau le retire : un brouillon qui se termine pendant la
            // signature le remettrait a false et laisserait repartir les brouillons suivants.
            if (replyStatus >= this.QUESTIONNAIRE_REPLY_STATUS_SHARED) {
              this._signingInProgress = false;
            }
          });
      } else {
        if (replyStatus >= this.QUESTIONNAIRE_REPLY_STATUS_SHARED) {
          this._signingInProgress = false;
        }
        // Sans notification ici, un clic sur "Signer" bloqué par la validation Mission/Activité
        // n'affichait rien du tout (silencieux) : on distingue les cas Mission/Activité (message
        // explicatif dédié) du cas générique (formValidator, message d'erreur déjà fourni par
        // l'appelant), pour guider l'utilisateur vers le champ manquant plutôt que de le
        // laisser deviner pourquoi rien ne se passe. Mission est prioritaire sur Activité en cas
        // de blocage simultané des deux blocs, à l'image de l'ordre déjà appliqué plus haut pour
        // formValidator.focusErrorInput().
        let errorMessage = error;
        if (missionErrors.length) {
          errorMessage = i18n.global.t("missionValidationError");
        } else if (activityErrors.length) {
          errorMessage = i18n.global.t("activityValidationError");
        }
        notificationManager.showNotification("error", errorMessage);
        resolve(validation.errors);
      }
    });

    if (replyStatus === this.QUESTIONNAIRE_REPLY_STATUS_DRAFTED) {
      // Chainer au lieu d'ecraser : deux brouillons concurrents (autosave sur plusieurs champs)
      // laisseraient sinon la signature n'attendre que le dernier, l'anterieur pouvant repondre apres.
      const chained = this._draftInFlight
        ? this._draftInFlight.then(
            () => resultPromise,
            () => resultPromise,
          )
        : resultPromise;
      this._draftInFlight = chained;
      chained.finally(() => {
        if (this._draftInFlight === chained) {
          this._draftInFlight = null;
        }
      });
    }

    return resultPromise;
  }

  processQuestionnaire(questionnaire) {
    const choicesToProcess = [
      "signedBy",
      "visibleBy",
      "marksVisibleBy",
      "interviewDateVisibleBy",
      "canBeCopiedBy",
      "triads",
      "crews",
      "skillSession",
      "secondaryQuestionnaires",
      "signedByStudeaManagerProfiles",
      "versionItemMarksVisibleBy",
    ];
    const fieldsToProcess = [
      "defaultApprenticeAccessStartDate",
      "defaultApprenticeAccessEndDate",
      "defaultTutorAccessStartDate",
      "defaultTutorAccessEndDate",
      "defaultApprenticeMasterAccessStartDate",
      "defaultApprenticeMasterAccessEndDate",
      "defaultStudeaManagerAccessStartDate",
      "defaultStudeaManagerAccessEndDate",
      "completenessDate",
      "firstReminder",
      "secondReminder",
    ];

    const collectionToProcess = ["summarySheetQuestionnaires"];

    choicesToProcess.forEach((field) => {
      let values = [];
      if (questionnaire[field] && Object.keys(questionnaire[field]).length) {
        Object.values(questionnaire[field]).forEach((choice) => {
          if (typeof choice === "string" || typeof choice === "number") {
            values.push(parseInt(choice));
          }
          if (choice.checked || choice.id) {
            let value = parseInt(choice.id ? choice.id : choice.value);
            if (["triads", "crews", "signedByStudeaManagerProfiles"].includes(field)) {
              value = { id: parseInt(choice.id ? choice.id : choice.value) };
            }
            values.push(value);
          }
          if (["secondaryQuestionnaires"].includes(field)) {
            values.push({ id: parseInt(choice.value) });
          }
        });
      }

      if (["skillSession"].includes(field)) {
        // In some case, skillSession field is not present in formType, so we have to reset skillSession value to null to avoid to pass an object to the post skillSession data
        if (!questionnaire[field]) {
          values = null;
        } else {
          values = questionnaire[field].data ? questionnaire[field].data : questionnaire[field];
        }
      }
      questionnaire[field] = values;
    });

    fieldsToProcess.forEach((field) => {
      if (!questionnaire[field]) {
        questionnaire[field] = null;
      }
    });

    collectionToProcess.forEach((field) => {
      if (questionnaire[field] && questionnaire[field].length) {
        questionnaire[field].forEach((item) => {
          item.summarySheet = { id: questionnaire.id };
        });
      }
    });

    if (questionnaire.questions && Object.keys(questionnaire.questions).length) {
      Object.values(questionnaire.questions).forEach((question) => {
        delete question["selected"];
        delete question["index"];

        if (question.questions && Object.keys(question.questions).length) {
          Object.values(question.questions).forEach((subQuestion) => {
            delete subQuestion["selected"];
            delete subQuestion["index"];
          });
        }
      });
    }

    return questionnaire;
  }

  updateFields(models, checkDates = false) {
    models = Object.values(models);
    const visibleBy = models.find((item) => item.vars.name === "visibleBy");

    const visibleByApprentice = visibleBy ? visibleBy.vars.value.find((item) => item.data === 1) : null;
    const visibleByTutor = visibleBy ? visibleBy.vars.value.find((item) => item.data === 2) : null;
    const visibleByApprenticeMaster = visibleBy ? visibleBy.vars.value.find((item) => item.data === 3) : null;

    const signedBy = models.find((item) => item.vars.name === "signedBy");
    const signedByApprentice = signedBy ? signedBy.vars.value.find((item) => item.data === 1) : null;

    const signedByTutor = signedBy ? signedBy.vars.value.find((item) => item.data === 2) : null;

    const signedByApprenticeMaster = signedBy ? signedBy.vars.value.find((item) => item.data === 3) : null;

    if (checkDates) {
      const defaultDate = models.find((item) => item.vars.name === "useSameDates");
      this.checkDates(models, signedBy, defaultDate && defaultDate.vars.value);
    }

    if (signedByApprentice) {
      signedByApprentice.disabled = !visibleByApprentice || !visibleByApprentice.checked;
      signedByApprentice.checked = signedByApprentice.disabled ? false : signedByApprentice.checked;
    }

    if (signedByTutor) {
      signedByTutor.disabled = !visibleByTutor || !visibleByTutor.checked;
      signedByTutor.checked = signedByTutor.disabled ? false : signedByTutor.checked;
    }

    if (signedByApprenticeMaster) {
      signedByApprenticeMaster.disabled = !visibleByApprenticeMaster || !visibleByApprenticeMaster.checked;
      signedByApprenticeMaster.checked = signedByApprenticeMaster.disabled ? false : signedByApprenticeMaster.checked;
    }

    const incidentSheet = models.find((item) => item.vars.name === "incidentSheet");

    const canBeCopiedBy = models.find((item) => item.vars.name === "canBeCopiedBy");

    if (canBeCopiedBy) {
      canBeCopiedBy.vars.required = incidentSheet && incidentSheet.vars.value;
    }
  }

  checkDates(models, signedBy, useDefault = false, questionnaire = null) {
    const startDates = [
      { field: "defaultApprenticeAccessStartDate", access: userManager.PROFILE_APPRENTICE },
      { field: "defaultTutorAccessStartDate", access: userManager.PROFILE_TUTOR },
      { field: "defaultApprenticeMasterAccessStartDate", access: userManager.PROFILE_APPRENTICE_MASTER },
      { field: "defaultStudeaManagerAccessStartDate", access: userManager.PROFILE_STUDEA_MANAGER },
    ];

    const endDates = [
      { field: "defaultApprenticeAccessEndDate", access: userManager.PROFILE_APPRENTICE },
      { field: "defaultTutorAccessEndDate", access: userManager.PROFILE_TUTOR },
      { field: "defaultApprenticeMasterAccessEndDate", access: userManager.PROFILE_APPRENTICE_MASTER },
      { field: "defaultStudeaManagerAccessEndDate", access: userManager.PROFILE_STUDEA_MANAGER },
    ];

    let defaultStartDate;
    let defaultEndDate;

    if (useDefault) {
      defaultStartDate = Object.values(models).find((item) => item.vars.name === "defaultStartDate");
      defaultEndDate = Object.values(models).find((item) => item.vars.name === "defaultEndDate");
    }

    const applyDateDefaults = (dates, defaultDate) => {
      dates.forEach((date) => {
        const field = Object.values(models).find((item) => item.vars.name === date.field);
        const signatory = Object.values(signedBy.vars.value).find((choice) => choice.data === date.access);
        field.vars.required = signatory && signatory.checked;

        if (defaultDate) {
          field.vars.value = signatory && signatory.checked ? defaultDate.vars.value : null;

          if (questionnaire) {
            questionnaire[date.field] = signatory && signatory.checked ? defaultDate.vars.value : null;
          }
        }
      });
    };

    applyDateDefaults(startDates, defaultStartDate);
    applyDateDefaults(endDates, defaultEndDate);
  }
}

export const questionnaireManager = new QuestionnaireManager();
